// Axios instances bound to each backend.
//
// Components must not import axios directly. Instead, call ``apiClient`` for
// the Django API (auth, writing domain, Stripe, collab JWT) and ``aiClient``
// for the FastAPI AI service. Every feature ships a thin wrapper module under
// ``src/util/<feature>Api.js`` that uses one of these instances — keep that
// layering so tests can mock a single surface and so network concerns (auth,
// logging, retries) stay in one place.
//
// Request lifecycle
// -----------------
// * The request interceptor reads the access token from sessionStorage
//   (``writeon:access_token``, written by ``util/auth.js``) and attaches it
//   as ``Authorization: Bearer``. Both clients get the same token — Django
//   mints it; FastAPI verifies it with the shared signing secret.
// * On a 401 response, ``apiClient`` tries one silent refresh via the
//   Django ``/api/auth/refresh/`` endpoint and retries the original request
//   once. Failing that, it clears the stored session and rejects — callers
//   typically redirect the user to /login.
// * The response interceptor logs every failure through the level-gated
//   ``logger`` (production defaults to ``warn`` so payloads don't leak).
import axios from 'axios';
import { AI_BASE, API_BASE } from './config';
import { logger } from './logger';

const ACCESS_KEY = 'writeon:access_token';
const REFRESH_KEY = 'writeon:refresh_token';
const USER_KEY = 'writeon:user';

// ``util/auth.js`` writes tokens into ``localStorage`` when the
// writer ticked "Remember me" and into ``sessionStorage`` for a
// plain login. Every helper below honours that convention by
// resolving the active store at call time — earlier versions only
// touched sessionStorage from the refresh path, which silently
// orphaned a remember-me login the first time the access token
// expired (refresh ran against an empty sessionStorage, returned
// null, ``clearSession`` then wiped sessionStorage but left the
// stale localStorage tokens behind, so every subsequent request
// 401'd against the dead token forever).
function safeStore(getStore) {
  try {
    return getStore();
  } catch {
    return null;
  }
}
function localStore() {
  return safeStore(() => window.localStorage);
}
function sessionStore() {
  return safeStore(() => window.sessionStorage);
}

// Returns whichever store currently holds the token, or
// ``localStorage`` as a default for a fresh refresh write that
// arrives before any login (rare — keeps the writer logged in
// across tab close just in case).
function activeTokenStore() {
  const ls = localStore();
  if (ls && ls.getItem(ACCESS_KEY)) return ls;
  const ss = sessionStore();
  if (ss && ss.getItem(ACCESS_KEY)) return ss;
  // Fall back to wherever the refresh token still lives — covers
  // the case where the access token has been wiped but the
  // refresh hasn't.
  if (ls && ls.getItem(REFRESH_KEY)) return ls;
  if (ss && ss.getItem(REFRESH_KEY)) return ss;
  return ls || ss || null;
}

function readAccessToken() {
  return (
    localStore()?.getItem(ACCESS_KEY) ||
    sessionStore()?.getItem(ACCESS_KEY) ||
    null
  );
}

function readRefreshToken() {
  return (
    localStore()?.getItem(REFRESH_KEY) ||
    sessionStore()?.getItem(REFRESH_KEY) ||
    null
  );
}

function attachAuth(config) {
  const token = readAccessToken();
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

function logFailure(error) {
  const status = error.response?.status;
  const url = error.config?.url;
  logger.warn('api_error', { status, url, message: error.message });
}

/**
 * Exchange the stored refresh token for a new pair. Runs at most once in
 * flight — concurrent 401s all await the same promise so we don't hammer
 * the refresh endpoint. Returns the new access token, or null on failure.
 */
let refreshInFlight = null;

function doRefresh() {
  if (refreshInFlight) return refreshInFlight;
  const refresh = readRefreshToken();
  if (!refresh) return Promise.resolve(null);
  refreshInFlight = axios
    .post(
      `${API_BASE}/api/auth/refresh/`,
      { refresh_token: refresh },
      { headers: { 'Content-Type': 'application/json' }, timeout: 10000 },
    )
    .then((resp) => {
      const { access_token, refresh_token } = resp.data ?? {};
      // Write the rotated pair back into whichever store currently
      // holds the user's session — see ``activeTokenStore`` for the
      // remember-me-vs-session resolution rules.
      const store = activeTokenStore();
      if (store && access_token) store.setItem(ACCESS_KEY, access_token);
      if (store && refresh_token) store.setItem(REFRESH_KEY, refresh_token);
      return access_token || null;
    })
    .catch(() => null)
    .finally(() => {
      refreshInFlight = null;
    });
  return refreshInFlight;
}

// Event fired once whenever ``clearSession`` wipes the stored tokens, so
// the React AuthContext can flip to signed-out without polling stale state.
// Subscribers: ``AuthProvider`` in ``src/auth/AuthContext.jsx``.
export const SESSION_CLEARED_EVENT = 'writeon:session-cleared';

function clearSession() {
  try {
    // Wipe BOTH stores. Earlier versions only cleared sessionStorage,
    // which left a remember-me login (tokens in localStorage) with
    // permanently-stale credentials after a refresh failure — every
    // subsequent request 401'd against a dead token forever.
    for (const store of [localStore(), sessionStore()]) {
      if (!store) continue;
      try {
        store.removeItem(ACCESS_KEY);
        store.removeItem(REFRESH_KEY);
        store.removeItem(USER_KEY);
      } catch {
        /* ignore per-store failures */
      }
    }
    // Dispatch unconditionally — ``CustomEvent`` is cheap and listeners
    // only act if they currently think they're signed in.
    window.dispatchEvent(new CustomEvent(SESSION_CLEARED_EVENT));
  } catch {
    /* ignore */
  }
}

// Per-client default timeouts. The Django gateway responds quickly
// (CRUD + auth) so 20 s is plenty there; the AI service may sit on
// a single Gemini/Bedrock call for up to ~93 s worst case (primary
// 45 s + retry sleeps + fallback 45 s). A 20 s axios default in
// front of a 45 s backend timeout silently killed every AI request
// that the model actually deliberated over — the worst possible
// failure mode, since the best AI answers are the slow ones.
// 120 s matches ``importApi.ANALYZE_AI_CHAT_TIMEOUT_MS`` so every
// AI surface has the same envelope.
const DJANGO_CLIENT_TIMEOUT_MS = 20000;
const AI_CLIENT_TIMEOUT_MS = 120000;

function createClient(baseURL, { autoRefresh = true, timeout = DJANGO_CLIENT_TIMEOUT_MS } = {}) {
  const instance = axios.create({ baseURL, withCredentials: false, timeout });
  instance.interceptors.request.use(attachAuth);

  instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;

      if (
        autoRefresh &&
        status === 401 &&
        original &&
        !original._retry &&
        // Don't loop on the refresh endpoint itself.
        !(original.url || '').includes('/api/auth/refresh/')
      ) {
        original._retry = true;
        const fresh = await doRefresh();
        if (fresh) {
          original.headers = { ...(original.headers || {}), Authorization: `Bearer ${fresh}` };
          return instance.request(original);
        }
        // Refresh failed; wipe the stored session so the next call doesn't
        // try again with the stale token.
        clearSession();
      }

      logFailure(error);
      return Promise.reject(error);
    },
  );

  return instance;
}

export const apiClient = createClient(API_BASE);
export const aiClient = createClient(AI_BASE, { timeout: AI_CLIENT_TIMEOUT_MS });

// Helpers to build standard form payloads.
export function toFormData(data) {
  const fd = new FormData();
  Object.entries(data ?? {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    fd.append(key, typeof value === 'object' ? JSON.stringify(value) : value);
  });
  return fd;
}
